import { CSSProperties } from 'react'

import { getContrastRatio, getLuminance, isHexCode } from '@syconium/little-miss-figgy'

import { ProductCatalogEmbroideryPlacement } from '../../../__generated__/graphql/catalog/graphql'
import { CartItem } from '../../../types/figs'
import {
  EmbroiderableProductEmbroideryOption,
  EmbroiderableProductEmbroideryRestrictions,
} from '../../../types/graphql/product'
import {
  B2bLogoFormValues,
  DtcAddLogoFormValues,
  DtcLogoSetupFormValues,
  EmbroideryFormKind,
  EmbroideryFormValues,
  IconFormValues,
  TextFormValues,
} from '../types'

import {
  genericTextEmbroideryFonts,
  genericTextEmbroideryThreadColors,
} from './embroideryThreadOptions'
import { isEmbroideryColorAllowedForProduct } from './isEmbroideryColorAllowedForProduct'
import { isEmbroideryFontAllowedForProduct } from './isEmbroideryFontAllowedForProduct'

// Initial logo form values for B2B logo embroidery form - All default/initial form values should
// be driven from the legacy portal/team store data that the user is associated with. See `useB2BEmbroiderySettings`
// to see how these initial values are derived for legacy portals & team store members.
const defaultB2bLogoFormValues: B2bLogoFormValues = {
  logoName: '',
  logoImageSource: '',
  logoPlacement: undefined,
}

function getDefaultThreadColorForProductEmbroideryOption(
  restrictions: EmbroiderableProductEmbroideryRestrictions,
  productColorHex: string | undefined | null
) {
  const textColor = isLightProductColor(productColorHex)
    ? genericTextEmbroideryThreadColors.Black
    : genericTextEmbroideryThreadColors.White

  if (
    isEmbroideryColorAllowedForProduct({
      productEmbroideryConfig: restrictions,
      colorKey: textColor.key,
    })
  ) {
    return textColor
  }

  return undefined
}

function getDefaultFontForProductEmbroideryOption(
  restrictions: EmbroiderableProductEmbroideryRestrictions
) {
  const textFont = genericTextEmbroideryFonts.Block

  if (
    isEmbroideryFontAllowedForProduct({
      productEmbroideryConfig: restrictions,
      fontKey: textFont.key,
    })
  ) {
    return textFont
  }

  return undefined
}

function defaultTextFormValues(
  embroideryOption: EmbroiderableProductEmbroideryOption,
  restrictions: EmbroiderableProductEmbroideryRestrictions,
  productColorHex: string | undefined
): TextFormValues {
  const textColor = getDefaultThreadColorForProductEmbroideryOption(restrictions, productColorHex)

  const defaultPlacement: ProductCatalogEmbroideryPlacement = {
    location: 'CHEST',
    position: 'LEFT',
  }

  const placement =
    embroideryOption.placements.find(placementOption => {
      return (
        placementOption.location === defaultPlacement.location &&
        placementOption.position === defaultPlacement.position
      )
    }) ?? embroideryOption.placements[0]

  return {
    textColor: textColor,
    font: getDefaultFontForProductEmbroideryOption(restrictions),
    firstLine: '',
    textPlacement: placement,
    secondLine: '',
  }
}

function defaultIconFormValues(
  embroideryOption: EmbroiderableProductEmbroideryOption,
  restrictions: EmbroiderableProductEmbroideryRestrictions,
  productColorHex: string | undefined
): IconFormValues {
  const textColor = getDefaultThreadColorForProductEmbroideryOption(restrictions, productColorHex)

  const defaultPlacement: ProductCatalogEmbroideryPlacement = {
    location: 'SLEEVE',
    position: 'LEFT',
  }

  const placement =
    embroideryOption.placements.find(placementOption => {
      return (
        placementOption.location === defaultPlacement.location &&
        placementOption.position === defaultPlacement.position
      )
    }) ?? embroideryOption.placements[0]

  return {
    // We always want to display the "flatlay-chest" overlay image for icon embroidery
    iconPlacement: placement,
    initials: '',
    initialsColor: textColor,
    iconImage: undefined,
  }
}

const defaultDtcLogoSetupFormValues: DtcLogoSetupFormValues = {
  name: undefined,
  notes: undefined,
  tos: undefined,
  file: undefined,
  digitization_setting: undefined,
}

function defaultDtcAddLogoFormValues(
  embroideryOption: EmbroiderableProductEmbroideryOption
): DtcAddLogoFormValues {
  const defaultPlacement: ProductCatalogEmbroideryPlacement = {
    location: 'CHEST',
    position: 'LEFT',
  }

  const placement =
    embroideryOption.placements.find(placementOption => {
      return (
        placementOption.location === defaultPlacement.location &&
        placementOption.position === defaultPlacement.position
      )
    }) ?? embroideryOption.placements[0]

  return {
    logoFileId: undefined,
    logoFile: undefined,
    logoPlacement: placement,
  }
}

export function mapEmbroideryOptionToInitialFormValues<T extends EmbroideryFormKind>(
  kind: T,
  embroideryOption: EmbroiderableProductEmbroideryOption,
  restrictions: EmbroiderableProductEmbroideryRestrictions,
  productColorHex: string | undefined
): EmbroideryFormValues[T] {
  const map: EmbroideryFormValues = {
    ICON: defaultIconFormValues(embroideryOption, restrictions, productColorHex),
    TEXT: defaultTextFormValues(embroideryOption, restrictions, productColorHex),
    LOGO_B2B: defaultB2bLogoFormValues,
    LOGO_SETUP: defaultDtcLogoSetupFormValues,
    LOGO_DTC: defaultDtcAddLogoFormValues(embroideryOption),
  } as const

  return map[kind]
}

export function hasLogoSetupLineForLogo(
  cartItems: Record<string, CartItem> | null | undefined,
  logoId: string | undefined
): boolean {
  if (!logoId) return false
  return Object.values(cartItems ?? {}).some(
    item => item.properties._is_logo_setup === 'true' && item.properties._logo_id === logoId
  )
}

export const allowedTextInputCharactersPattern = /^[!@#$%&*()+=\-:“;’'<>.?,\/a-zA-Z0-9\s]*$/
export const allowedIconTextInputCharactersPattern = /^[A-Z]+$/
export const allowedLogoNameInputCharactersPattern = /^[a-zA-Z0-9\s'-.]+$/

/**
 * Default maximum character count for embroidery text fields.
 * Used as a fallback when the value isn't provided by the API.
 */
export const EMBROIDERY_TEXT_MAX_CHARS = 26

export function isLightProductColor(hexColor: CSSProperties['color'] | null): boolean {
  if (!hexColor) return false
  if (hexColor === 'white') return true
  if (typeof hexColor === 'string' && hexColor.startsWith('#')) {
    try {
      const luminance = getLuminance(hexColor)
      return luminance > 0.5
    } catch {
      return false
    }
  }

  return false
}

export function isSufficientEmbroideryColorContrast(
  hexColor1: CSSProperties['color'],
  hexColor2: CSSProperties['color']
): boolean {
  if (!isHexCode(hexColor1) || !isHexCode(hexColor2)) {
    return false
  }

  try {
    const contrast = getContrastRatio(hexColor1, hexColor2)

    // Setting an arbitrary 1.3:1 contrast ratio, as there are no guidelines for thread color
    // contrast. Feel free to alter this as product needs demand.
    return contrast >= 2.25
  } catch {
    return false
  }
}
